import React from 'react';
import { Activity, PhoneCall, ShieldCheck, AlertTriangle, TrendingUp, Sparkles, Headphones, Users, FileX, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { VoiceLayout } from './VoiceLayout';
import { VOICE_AGENTS, VOICE_ACTIVITY_FEED, AI_CAMPAIGNS } from '@/data/voiceAgents';
import { usePageTitle } from '@/lib/usePageTitle';
import { formatNumber, formatSAR, formatPct } from '@/lib/format';
import { cn } from '@/lib/utils';

const KPI_GRID = [
  { id: 'agents',  label: 'Active AI agents',         value: 4,        format: 'num', trend: 0,  hint: 'of 6 total' },
  { id: 'calls',   label: 'Calls handled today',      value: 970,      format: 'num', trend: 18 },
  { id: 'verif',   label: 'Verified debtors',         value: 842,      format: 'num', trend: 22, hint: '87% pass-rate' },
  { id: 'ptp',     label: 'Promises to pay captured', value: 342,      format: 'num', trend: 12 },
  { id: 'plans',   label: 'Plans created',            value: 178,      format: 'num', trend: 9 },
  { id: 'disp',    label: 'Disputes detected',        value: 24,       format: 'num', trend: 4 },
  { id: 'hard',    label: 'Hardship cases',           value: 18,       format: 'num', trend: 7 },
  { id: 'flag',    label: 'Compliance flags',         value: 6,        format: 'num', trend: -3, hint: 'investigating' },
  { id: 'esc',     label: 'Human escalations',        value: 41,       format: 'num', trend: -8 },
  { id: 'dur',     label: 'Avg call duration',        value: '2:14',   format: 'raw', hint: 'min:sec' },
  { id: 'cr',      label: 'Contact rate',             value: 0.62,     format: 'pct', trend: 5 },
  { id: 'rpc',     label: 'Right-party contact',      value: 0.71,     format: 'pct', trend: 3 },
  { id: 'ptpc',    label: 'PTP conversion',           value: 0.41,     format: 'pct', trend: 6 },
  { id: 'hrs',     label: 'Agent-hours saved (today)',value: 312,      format: 'num', trend: 14 },
];

const CHARTS = [
  { id: 'outcomes', title: 'Calls by outcome',
    rows: [
      { l: 'Promise to Pay',       v: 342, c: 'success' },
      { l: 'Plan Created',         v: 178, c: 'primary' },
      { l: 'Paid in Full',         v: 64,  c: 'success' },
      { l: 'Hardship Captured',    v: 18,  c: 'info' },
      { l: 'Dispute Captured',     v: 24,  c: 'warning' },
      { l: 'Escalated to Human',   v: 41,  c: 'warning' },
      { l: 'Wrong Party',          v: 12,  c: 'muted' },
      { l: 'Refused / DNC',        v: 9,   c: 'destructive' },
    ] },
  { id: 'funnel', title: 'AI vs Human recovery funnel',
    rows: [
      { l: 'Dialled',          ai: 1480, human: 380 },
      { l: 'Connected',        ai: 970,  human: 240 },
      { l: 'Verified',         ai: 842,  human: 198 },
      { l: 'Balance presented',ai: 786,  human: 184 },
      { l: 'Option accepted',  ai: 542,  human: 121 },
      { l: 'Promise captured', ai: 342,  human: 78  },
      { l: 'Payment confirmed',ai: 64,   human: 19  },
    ] },
  { id: 'hours', title: 'Calls by hour (KSA)',
    bars: [9,10,11,12,13,14,15,16,17,18,19,20].map((h, i) => ({ l: `${h}:00`, v: Math.round(30 + Math.sin(i / 2) * 35 + 60) })) },
  { id: 'topAgents', title: 'Top performing agents (today)',
    rows: VOICE_AGENTS.filter((a) => a.callsToday > 0).sort((a, b) => b.successRate - a.successRate).map((a) => ({ l: a.name, v: a.successRate, c: 'primary' })) },
  { id: 'flags', title: 'Compliance flags by type',
    rows: [
      { l: 'Contact-window breach', v: 2, c: 'destructive' },
      { l: 'Frequency cap',         v: 2, c: 'warning' },
      { l: 'Identity not verified', v: 1, c: 'warning' },
      { l: 'Banned phrase',         v: 1, c: 'destructive' },
    ] },
  { id: 'ptpByCampaign', title: 'Promise-to-pay amount by campaign (SAR)',
    rows: AI_CAMPAIGNS.filter((c) => c.successRate > 0).slice(0, 5).map((c) => ({ l: c.name.split('—')[0].trim(), v: Math.round(c.debtors * c.successRate * 18), c: 'primary' })) },
  { id: 'escReason', title: 'Escalations by reason',
    rows: [
      { l: 'Hardship',          v: 18, c: 'info' },
      { l: 'Dispute',           v: 24, c: 'warning' },
      { l: 'Angry sentiment',   v: 12, c: 'destructive' },
      { l: 'Vulnerable customer', v: 5, c: 'info' },
      { l: 'Repeated verif. fail', v: 8, c: 'warning' },
    ] },
];

export default function VoiceDashboard() {
  usePageTitle('AI Voice Agents');
  return (
    <VoiceLayout
      title="AI voice operations dashboard"
      subtitle="Executive view of every AI-driven debtor conversation — outcomes, compliance, recovery impact and live activity in one cockpit."
      actions={(<>
        <Button size="sm" variant="outline"><Activity className="h-4 w-4 mr-1.5" aria-hidden="true" />Live</Button>
        <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90"><Sparkles className="h-4 w-4 mr-1.5" aria-hidden="true" />Spin up new agent</Button>
      </>)}
    >
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="voice-kpi-grid">
        {KPI_GRID.map((k) => (
          <div key={k.id} className="surface-card p-3.5">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">{k.label}</p>
            <p className="font-mono-num text-xl font-semibold mt-1">
              {k.format === 'sar' ? formatSAR(k.value) : k.format === 'pct' ? formatPct(k.value) : k.format === 'num' ? formatNumber(k.value) : k.value}
            </p>
            <div className="flex items-center justify-between mt-1">
              {k.trend != null ? (
                <span className={cn('text-[10px] font-medium', k.trend >= 0 ? 'kpi-trend-up' : 'kpi-trend-down')}>
                  {k.trend >= 0 ? '↑' : '↓'} {Math.abs(k.trend)}%
                </span>
              ) : <span />}
              {k.hint && <span className="text-[10px] text-muted-foreground truncate">{k.hint}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Charts grid + live feed */}
      <div className="grid lg:grid-cols-3 gap-4">
        {CHARTS.slice(0, 6).map((c) => (
          <div key={c.id} className="surface-card p-4 lg:col-span-1">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">{c.title}</p>
            {c.bars ? <Bars data={c.bars} /> : c.rows[0]?.ai != null ? <Funnel rows={c.rows} /> : <RowList rows={c.rows} unit={c.id === 'topAgents' ? 'pct' : c.id === 'ptpByCampaign' ? 'sar' : 'num'} />}
          </div>
        ))}
        <div className="surface-card p-4 lg:col-span-2">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Escalations by reason</p>
          <RowList rows={CHARTS[6].rows} unit="num" />
        </div>
        <ActivityFeed />
      </div>
    </VoiceLayout>
  );
}

const Bars = ({ data }) => {
  const max = Math.max(...data.map((d) => d.v));
  return (
    <div className="flex items-stretch gap-1 h-32" role="img" aria-label="Calls by hour of day">
      {data.map((d) => (
        <div key={d.l} className="flex-1 h-full flex flex-col justify-end items-center gap-1">
          <div className="w-full bg-primary/70 rounded-t" style={{ height: `${(d.v / max) * 100}%` }} title={`${d.l} · ${d.v}`} />
          <span className="text-[9px] text-muted-foreground">{d.l.split(':')[0]}</span>
        </div>
      ))}
    </div>
  );
};

const RowList = ({ rows, unit = 'num' }) => {
  const max = Math.max(...rows.map((r) => r.v));
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.l} className="flex items-center gap-2">
          <span className="text-[11px] w-32 truncate">{r.l}</span>
          <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
            <div className={cn('h-full',
              r.c === 'primary' ? 'bg-primary' :
              r.c === 'success' ? 'bg-success' :
              r.c === 'warning' ? 'bg-warning' :
              r.c === 'destructive' ? 'bg-destructive' :
              r.c === 'info' ? 'bg-info' : 'bg-muted-foreground/40'
            )} style={{ width: `${(r.v / max) * 100}%` }} />
          </div>
          <span className="font-mono-num text-[11px] w-16 text-right">
            {unit === 'pct' ? `${r.v}%` : unit === 'sar' ? formatSAR(r.v, { compact: r.v > 100000 }) : formatNumber(r.v)}
          </span>
        </div>
      ))}
    </div>
  );
};

const Funnel = ({ rows }) => {
  const max = Math.max(...rows.map((r) => r.ai));
  return (
    <div className="space-y-1.5" role="img" aria-label="AI versus human recovery funnel">
      {rows.map((r) => (
        <div key={r.l}>
          <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
            <span>{r.l}</span>
            <span className="font-mono-num">AI {formatNumber(r.ai)} · Human {formatNumber(r.human)}</span>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden flex">
            <div className="h-full bg-primary/80" style={{ width: `${(r.ai / max) * 80}%` }} />
            <div className="h-full bg-onyx/40" style={{ width: `${(r.human / max) * 20}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const ActivityFeed = () => (
  <div className="surface-card p-4 lg:col-span-1">
    <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Live activity</p>
    <ul className="space-y-2.5">
      {VOICE_ACTIVITY_FEED.map((e) => (
        <li key={e.id} className="flex items-start gap-2 text-[12px] leading-snug">
          <span className={cn('mt-1 h-1.5 w-1.5 rounded-full shrink-0',
            e.kind === 'success' && 'bg-success',
            e.kind === 'warn'    && 'bg-warning',
            e.kind === 'danger'  && 'bg-destructive',
            e.kind === 'info'    && 'bg-info',
          )} />
          <div className="flex-1">
            <p>{e.text}</p>
            <p className="text-[10px] text-muted-foreground">{e.when}</p>
          </div>
        </li>
      ))}
    </ul>
  </div>
);
